import React from 'react';
import { useAuthStore } from '../stores/authStore';
import { collection, query, where, orderBy } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useCollection } from 'react-firebase-hooks/firestore';
import { Download, Clock, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/Button';

export const UserDashboard: React.FC = () => {
  const { user } = useAuthStore();
  const [orders, loading, error] = useCollection(
    user ? query(
      collection(db, 'orders'),
      where('userId', '==', user.uid),
      orderBy('createdAt', 'desc')
    ) : null
  );

  if (loading) {
    return (
      <div className="min-h-screen pt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex justify-center">
            <Clock className="w-6 h-6 animate-spin text-red-600" />
            <span className="ml-2">Loading your videos...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen pt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="bg-red-50 text-red-600 p-4 rounded-lg flex items-center">
            <AlertCircle className="w-5 h-5 mr-2" />
            <span>Error loading your videos: {error.message}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">My Videos</h1>
          <Button variant="primary" onClick={() => window.location.href = '/get-started'}>
            Create New Video
          </Button>
        </div>

        {!orders?.docs.length ? (
          <div className="text-center py-12 bg-white rounded-lg shadow">
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No videos yet
            </h3>
            <p className="text-gray-500 mb-4">
              Start creating your magical Santa video today!
            </p>
            <Button variant="primary" onClick={() => window.location.href = '/get-started'}>
              Create Your First Video
            </Button>
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {orders.docs.map((doc) => {
              const order = doc.data();
              return (
                <div key={doc.id} className="bg-white rounded-lg shadow overflow-hidden">
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <span className={`px-3 py-1 text-sm font-semibold rounded-full flex items-center
                        ${order.status === 'completed' ? 'bg-green-100 text-green-800' : 
                          order.status === 'processing' ? 'bg-yellow-100 text-yellow-800' : 
                          'bg-gray-100 text-gray-800'}`}>
                        {order.status === 'completed' ? (
                          <>
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Completed
                          </>
                        ) : order.status === 'processing' ? (
                          <>
                            <Clock className="w-4 h-4 mr-1" />
                            Processing
                          </>
                        ) : (
                          <>
                            <AlertCircle className="w-4 h-4 mr-1" />
                            Pending
                          </>
                        )}
                      </span>
                      <span className="text-sm text-gray-500">
                        {order.createdAt?.toDate().toLocaleDateString()}
                      </span>
                    </div>

                    {order.generatedVideoUrl ? (
                      <Button
                        variant="primary"
                        className="w-full"
                        onClick={() => window.open(order.generatedVideoUrl, '_blank')}
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Download Video
                      </Button>
                    ) : (
                      <p className="text-sm text-gray-600">
                        {order.status === 'processing' 
                          ? 'Your video is being processed...'
                          : 'Waiting to start processing...'}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};